"use client"

import { useState, useEffect, useMemo, useCallback, useRef, forwardRef, useImperativeHandle } from "react"
import * as XLSX from "xlsx"
import { renderToStaticMarkup } from "react-dom/server"

const GdTable = forwardRef(({
    columns,
    data,
    groupHeaders = [],
    itemsPerPageOptions = [10, 25, 50, 100],
    defaultItemsPerPage = 10,
    tableClassName = "table table-bordered",
    containerClassName = "data-table-container",
    initialSort = null,
    showCsvExport = true,
    stickyHeader = true,
    stickyTop = 60,
    height = 600,            // compat
    headerHeight = 50,
    separadorPorCampo = null,
    getRowClass = () => "",
    // ====== VIRTUALIZACIÓN ======
    virtualize = false,      // 👈 activar/desactivar virtualización (default false)
    rowHeight = 40,          // alto fijo por fila al virtualizar
    overscan = 12,            // filas extra arriba/abajo al virtualizar
    exportFilename = "tabla-datos.xlsx",
    showActiveFilters = true, // panel de filtros/orden activos a la izquierda de la barra superior
    loading = false, // muestra un overlay de "Cargando..." sobre la tabla mientras el padre está pidiendo datos
    maxRows = null, // si se define, la tabla (y su export) solo consideran las primeras N filas de `data`
    onBeforeExport = null, // (data) => boolean. Si devuelve false, cancela el export (el padre decide qué hacer, ej. mostrar un modal). Se puede reintentar el export igual vía ref.exportNow().
}, ref) => {
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage)
    const [globalFilter, setGlobalFilter] = useState("")
    const [columnFilters, setColumnFilters] = useState({})
    const [sortConfig, setSortConfig] = useState(() => {
        if (initialSort && columns[initialSort[0]]) {
            return { key: columns[initialSort[0]].name, direction: initialSort[1] }
        }
        return null
    })

    useEffect(() => setCurrentPage(1), [globalFilter, columnFilters, itemsPerPage])

    // El initialSort del useState solo corre en el primer render: si las columnas aún no
    // estaban listas (o si initialSort depende de un estado que cambia después) el orden
    // por defecto nunca se aplicaba. Lo re-aplicamos mientras el usuario no haya ordenado a mano.
    const initialSortKey = initialSort && columns[initialSort[0]] ? columns[initialSort[0]].name : null
    const initialSortDirection = initialSort ? initialSort[1] : null
    const usuarioOrdenoManual = useRef(false)

    useEffect(() => {
        if (usuarioOrdenoManual.current || !initialSortKey) return
        setSortConfig(prev => (
            prev && prev.key === initialSortKey && prev.direction === initialSortDirection
                ? prev
                : { key: initialSortKey, direction: initialSortDirection }
        ))
    }, [initialSortKey, initialSortDirection])

    const normalize = useCallback(
        str =>
            String(str || "")
                .normalize("NFD")
                .replace(/[̀-ͯ]/g, "")
                .toLowerCase(),
        []
    )

    // Búsqueda por palabras: "luis gajardo" debe encontrar "Luis Felipe Gajardo
    // Osorio" aunque "felipe" quede en el medio. Se exige que CADA palabra
    // buscada esté presente en el valor, sin importar el orden ni que sean
    // contiguas (antes se exigía el texto completo como un solo substring).
    const matchesQuery = useCallback(
        (value, query) => {
            const tokens = normalize(query).split(/\s+/).filter(Boolean)
            if (tokens.length === 0) return true
            const normalizedValue = normalize(value)
            return tokens.every(token => normalizedValue.includes(token))
        },
        [normalize]
    )

    const limitedData = useMemo(() => (
        typeof maxRows === "number" ? data.slice(0, maxRows) : data
    ), [data, maxRows])

    const filteredData = useMemo(() => {
        let filtered = limitedData.filter(row => {
            if (globalFilter) {
                const matches = columns.some(col => matchesQuery(col.getFilterValue ? col.getFilterValue(row) : row[col.name], globalFilter))
                if (!matches) return false
            }
            for (const col of columns) {
                const filterValue = columnFilters[col.name]
                if (!filterValue) continue

                const value = col.getFilterValue ? col.getFilterValue(row) : row[col.name]
                if (!matchesQuery(String(value), filterValue)) return false
            }
            return true
        })

        if (sortConfig) {
            const { key, direction } = sortConfig
            filtered = [...filtered].sort((a, b) => {
                const aVal = a[key] ?? ""
                const bVal = b[key] ?? ""
                if (typeof aVal === "number" && typeof bVal === "number") {
                    return direction === "asc" ? aVal - bVal : bVal - aVal
                }
                return direction === "asc"
                    ? String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: "base" })
                    : String(bVal).localeCompare(String(aVal), undefined, { numeric: true, sensitivity: "base" })
            })
        }

        return filtered
    }, [limitedData, globalFilter, columnFilters, columns, sortConfig, matchesQuery])

    const totalPages = Math.ceil(filteredData.length / itemsPerPage)
    const startIdx = (currentPage - 1) * itemsPerPage
    const paginatedData = filteredData.slice(startIdx, startIdx + itemsPerPage)

    // const exportToCSV = useCallback(() => {
    //     const excludedTitles = ["Acciones", "Accion", "Acción"]
    //     const exportableColumns = columns.filter(col => !excludedTitles.includes(col.title))

    //     const wsData = [
    //         exportableColumns.map(col => col.title),
    //         ...filteredData.map(row =>
    //             exportableColumns.map(col => {
    //                 try {
    //                     let value
    //                     if (col.render) {
    //                         const rendered = col.render(row)
    //                         if (typeof rendered === "string" || typeof rendered === "number") {
    //                             value = rendered.toString()
    //                         } else {
    //                             let htmlString = renderToStaticMarkup(rendered)
    //                             value = stripHtmlTags(htmlString)
    //                         }
    //                     } else {
    //                         value = row[col.name]
    //                     }
    //                     value = stripHtmlTags(value?.toString() ?? "")
    //                     const cleaned = value.replace(/[$.]/g, "").replace(",", ".")
    //                     if (/^\d+(\.\d+)?$/.test(cleaned)) return Number(cleaned)
    //                     return value
    //                 } catch {
    //                     return ""
    //                 }
    //             })
    //         )
    //     ]

    //     const worksheet = XLSX.utils.aoa_to_sheet(wsData)
    //     const workbook = XLSX.utils.book_new()
    //     XLSX.utils.book_append_sheet(workbook, worksheet, "Datos")
    //     XLSX.writeFile(workbook, "tabla-datos.xlsx")
    // }, [columns, filteredData])

    const runExport = useCallback(() => {
        const excludedTitles = ["Acciones", "Accion", "Acción"];
        const exportableColumns = columns.filter(col => !excludedTitles.includes(col.title));

        // ==================================
        // Función que genera el serial Excel sin hora
        // ==================================
        const excelSerial = (day, month, year) => {
            const date = new Date(Date.UTC(year, month - 1, day));
            return (date - new Date(Date.UTC(1899, 11, 30))) / 86400000;
        };

        const wsData = [
            exportableColumns.map(col => col.title),
            ...filteredData.map(row =>
                exportableColumns.map(col => {
                    try {
                        // Obtener valor crudo (texto o render)
                        let raw = col.render
                            ? (() => {
                                const rendered = col.render(row);
                                if (typeof rendered === "string" || typeof rendered === "number") return String(rendered);

                                const html = renderToStaticMarkup(rendered);
                                return stripHtmlTags(html);
                            })()
                            : row[col.name];

                        raw = stripHtmlTags(String(raw ?? ""));

                        // ==================================
                        // TIPO: FECHA
                        // ==================================
                        if (col.type === "date") {
                            let d, m, y;

                            if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
                                // dd-mm-yyyy
                                [d, m, y] = raw.split("-");
                            } else if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                                // yyyy-mm-dd
                                [y, m, d] = raw.split("-");
                            } else {
                                return raw; // no es fecha → texto
                            }

                            const serial = excelSerial(Number(d), Number(m), Number(y));
                            return serial; // fecha SIN hora
                        }

                        // ==================================
                        // TIPO: NUMERO
                        // ==================================
                        if (col.type === "number") {
                            // Mantener solo dígitos, eliminar $ . , - espacios y todo lo demás
                            const cleaned = raw.replace(/[^\d]/g, "");

                            // Convertir a número entero
                            const n = Number(cleaned);

                            // Si se puede convertir, devolver número puro
                            return isNaN(n) ? raw : n;
                        }



                        // ==================================
                        // GENERAL (texto)
                        // ==================================
                        return raw;

                    } catch (err) {
                        console.error("Error exportando:", err);
                        return "";
                    }
                })
            )
        ];

        // Crear hoja
        const worksheet = XLSX.utils.aoa_to_sheet(wsData);
        const workbook = XLSX.utils.book_new();

        // ==================================
        // FORMATO DE FECHAS (sin hora)
        // ==================================
        const range = XLSX.utils.decode_range(worksheet["!ref"]);

        for (let C = 0; C < exportableColumns.length; C++) {
            const col = exportableColumns[C];

            if (col.type === "date") {
                const fmt = col.dateFormat || "dd-mm-yyyy";

                for (let R = 1; R <= range.e.r; R++) {
                    const ref = XLSX.utils.encode_cell({ r: R, c: C });
                    const cell = worksheet[ref];

                    // Solo aplicar formato a celdas numéricas (serial Excel)
                    if (cell && cell.t === "n") {
                        cell.z = fmt;
                    }
                }
            }
        }

        XLSX.utils.book_append_sheet(workbook, worksheet, "Datos");

        // 👇 Nombre de archivo configurable
        XLSX.writeFile(workbook, exportFilename);

    }, [columns, filteredData, exportFilename]);

    // El botón "Exportar Datos" pasa primero por onBeforeExport (si se pasó).
    // Si devuelve false, se cancela: el padre decide qué hacer (ej. mostrar un
    // modal de advertencia) y puede forzar el export igual llamando a
    // ref.current.exportNow(), que salta este chequeo.
    const exportToCSV = useCallback(() => {
        if (onBeforeExport && onBeforeExport(filteredData) === false) return;
        runExport();
    }, [onBeforeExport, filteredData, runExport]);

    useImperativeHandle(ref, () => ({
        exportNow: runExport,
    }), [runExport]);

    function stripHtmlTags(str) {
        if (!str || typeof str !== "string") return ""
        return str.replace(/<[^>]*>/g, "").trim()
    }

    const handleColumnFilterChange = useCallback((col, val) => {
        setColumnFilters(prev => ({ ...prev, [col]: val }))
    }, [])

    const handleSort = useCallback(col => {
        usuarioOrdenoManual.current = true
        setSortConfig(prev => {
            if (!prev || prev.key !== col) return { key: col, direction: "asc" }
            if (prev.direction === "asc") return { key: col, direction: "desc" }
            return null
        })
    }, [])

    const renderSortIcon = col => {
        if (!sortConfig || sortConfig.key !== col) return " ⇅"
        return sortConfig.direction === "asc" ? " ↑" : " ↓"
    }

    // Vuelve al ordenamiento con el que nace la tabla (o a ninguno si no tenía).
    const restaurarOrdenPorDefecto = useCallback(() => {
        usuarioOrdenoManual.current = false
        setSortConfig(initialSortKey ? { key: initialSortKey, direction: initialSortDirection } : null)
    }, [initialSortKey, initialSortDirection])

    // ========= FILTROS / ORDENAMIENTO ACTIVOS =========
    const columnTitleByName = useMemo(() => {
        const map = {}
        columns.forEach(col => {
            if (col.name && !map[col.name]) map[col.name] = col.title || col.name
        })
        return map
    }, [columns])

    const filtrosActivos = useMemo(() => {
        const items = []

        if (String(globalFilter).trim()) {
            items.push({
                id: "__global__",
                icono: "bi-search",
                clase: "bg-info text-dark",
                label: "Búsqueda",
                valor: String(globalFilter).trim(),
                onClear: () => setGlobalFilter("")
            })
        }

        Object.entries(columnFilters).forEach(([name, valor]) => {
            if (!valor || !String(valor).trim()) return
            items.push({
                id: `col-${name}`,
                icono: "bi-funnel-fill",
                clase: "bg-primary",
                label: columnTitleByName[name] || name,
                valor: String(valor).trim(),
                onClear: () => handleColumnFilterChange(name, "")
            })
        })

        // El orden por defecto de la tabla no se lista: solo aparece si el usuario lo cambió.
        const esOrdenPorDefecto = Boolean(
            sortConfig
            && initialSortKey
            && sortConfig.key === initialSortKey
            && sortConfig.direction === initialSortDirection
        )

        if (sortConfig && !esOrdenPorDefecto) {
            items.push({
                id: "__sort__",
                icono: sortConfig.direction === "asc" ? "bi-sort-down-alt" : "bi-sort-down",
                clase: "bg-dark",
                label: "Orden",
                valor: `${columnTitleByName[sortConfig.key] || sortConfig.key} ${sortConfig.direction === "asc" ? "↑" : "↓"}`,
                onClear: restaurarOrdenPorDefecto
            })
        }

        return items
    }, [globalFilter, columnFilters, sortConfig, columnTitleByName, handleColumnFilterChange, initialSortKey, initialSortDirection, restaurarOrdenPorDefecto])

    const limpiarFiltros = useCallback(() => {
        setGlobalFilter("")
        setColumnFilters({})
        restaurarOrdenPorDefecto()
    }, [restaurarOrdenPorDefecto])

    const renderPagination = () => {
        const pages = []
        const maxVis = 5
        let start = Math.max(1, currentPage - Math.floor(maxVis / 2))
        let end = Math.min(totalPages, start + maxVis - 1)
        if (end - start + 1 < maxVis) start = Math.max(1, end - maxVis + 1)

        const pageItem = (num, label = num) => (
            <li key={num} className={`page-item ${currentPage === num ? "active" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(num)}>{label}</button>
            </li>
        )

        pages.push(
            <li key="prev" className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>&laquo;</button>
            </li>
        )
        if (start > 1) {
            pages.push(pageItem(1))
            if (start > 2) pages.push(<li key="ell1" className="page-item disabled"><span className="page-link">...</span></li>)
        }
        for (let i = start; i <= end; i++) pages.push(pageItem(i))
        if (end < totalPages) {
            if (end < totalPages - 1) pages.push(<li key="ell2" className="page-item disabled"><span className="page-link">...</span></li>)
            pages.push(pageItem(totalPages))
        }
        pages.push(
            <li key="next" className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>&raquo;</button>
            </li>
        )
        return <nav><ul className="pagination">{pages}</ul></nav>
    }

    // ========= VIRTUALIZACIÓN CON SCROLL DE PÁGINA (altura fija) =========
    const tbodyRef = useRef(null)
    const [range, setRange] = useState({ start: 0, end: 0 })

    const recalcRange = useCallback(() => {
        if (!virtualize) return
        const N = paginatedData.length
        if (!tbodyRef.current || N === 0) {
            setRange(prev => (prev.start !== 0 || prev.end !== 0 ? { start: 0, end: 0 } : prev))
            return
        }

        const rect = tbodyRef.current.getBoundingClientRect()
        const bodyTop = window.scrollY + rect.top
        const viewTop = window.scrollY
        const viewBottom = viewTop + window.innerHeight

        const startY = Math.max(viewTop, bodyTop)
        const endY = Math.min(viewBottom, bodyTop + N * rowHeight)
        const visible = Math.max(0, endY - startY)

        let first = Math.floor((startY - bodyTop) / rowHeight)
        first = isFinite(first) ? Math.max(0, Math.min(N - 1, first)) : 0

        const count = Math.ceil(visible / rowHeight)
        let last = Math.min(N, first + count + overscan)
        first = Math.max(0, first - overscan)

        setRange(prev =>
            prev.start !== first || prev.end !== last
                ? { start: first, end: last }
                : prev
        )
    }, [virtualize, paginatedData.length, rowHeight, overscan])

    // Listeners de scroll/resize solo en modo virtualizado
    useEffect(() => {
        if (!virtualize) return
        const onScroll = () => requestAnimationFrame(recalcRange)
        const onResize = () => requestAnimationFrame(recalcRange)
        window.addEventListener("scroll", onScroll, { passive: true })
        window.addEventListener("resize", onResize)
        recalcRange()
        return () => {
            window.removeEventListener("scroll", onScroll)
            window.removeEventListener("resize", onResize)
        }
    }, [virtualize, recalcRange])

    // Recalcular cuando cambia la cantidad de filas (paginadas)
    useEffect(() => {
        if (virtualize) recalcRange()
    }, [virtualize, recalcRange, paginatedData.length])

    const { start, end } = range
    const topSpacer = virtualize ? start * rowHeight : 0
    const bottomSpacer = virtualize ? Math.max(0, (paginatedData.length - end) * rowHeight) : 0

    // Filas visibles (virtualizado)
    const renderVirtualRows = () => {
        const rows = []
        for (let i = start; i < end; i++) {
            const row = paginatedData[i]
            if (!row) continue

            const prev = paginatedData[i - 1]
            const showSeparator = separadorPorCampo && i > 0 && row?.[separadorPorCampo] !== prev?.[separadorPorCampo]

            rows.push(
                <tr
                    key={i}
                    className={getRowClass(row, i)}
                    style={{
                        height: rowHeight,
                        // usar sombra en vez de borderTop para no alterar altura
                        boxShadow: showSeparator ? "inset 0 3px 0 0 #000" : "none"
                    }}
                >
                    {columns.map((col, ci) => (
                        <td
                            key={`${i}-${ci}`}
                            className={col.cellClassName || ""}
                            style={{
                                width: col.width || "auto",
                                minWidth: col.width || "auto",
                                maxWidth: col.width || "auto",
                                // por defecto: fila de altura fija con texto en una línea
                                // whiteSpace: col.allowOverflow ? "nowrap" : "nowrap",
                                // overflow: col.allowOverflow ? "visible" : "hidden",
                                // textOverflow: col.allowOverflow ? "clip" : "ellipsis",
                                // position: col.allowOverflow ? "relative" : undefined,
                                // zIndex: col.allowOverflow ? 4500 : "auto",
                            }}
                        >
                            {col.render ? col.render(row) : row[col.name]}
                        </td>
                    ))}
                </tr>
            )
        }
        return rows
    }

    // Filas normales (no virtualizado)
    const renderNormalRows = () => {
        return paginatedData.flatMap((row, ri) => {
            const prev = paginatedData[ri - 1]
            const showSeparator = separadorPorCampo && ri > 0 && row?.[separadorPorCampo] !== prev?.[separadorPorCampo]

            const sep = showSeparator ? (
                <tr key={`sep-${ri}`} className="separador-negro">
                    <td colSpan={columns.length} style={{ padding: 0 }}>
                        <div style={{ borderTop: "3px solid black" }} />
                    </td>
                </tr>
            ) : null

            const tr = (
                <tr key={ri} className={getRowClass(row, ri)}>
                    {columns.map((col, ci) => (
                        <td
                            key={`${ri}-${ci}`}
                            className={col.cellClassName || ""}
                            style={{
                                width: col.width || "auto",
                                minWidth: col.width || "auto",
                                maxWidth: col.width || "auto",
                                // whiteSpace: "normal",
                                // wordBreak: "break-word",
                                // overflow: col.allowOverflow ? "visible" : "visible",
                                // position: col.allowOverflow ? "relative" : undefined,
                                // zIndex: col.allowOverflow ? 500 : "auto"
                            }}
                        >
                            {col.render ? col.render(row) : row[col.name]}
                        </td>
                    ))}
                </tr>
            )

            return sep ? [sep, tr] : [tr]
        })
    }

    const thBaseStyle = { cursor: "pointer" }
    const removedTableClasses = new Set(["table-striped", "table-hover", "table-sm", "table-borderless", "gd-table-p5"])
    const normalizedTableClassName = Array.from(new Set([
        ...String(tableClassName || "").split(/\s+/).filter(cls => cls && !removedTableClasses.has(cls)),
        "table",
        "table-bordered",
        "gd-calendar-table-style",
    ])).join(" ")

    return (
        <div className={containerClassName}>
            <div className="row mb-3 justify-content-end align-items-center g-2">
                {showActiveFilters && (
                    <div className="col-md-6">
                        <div
                            className={`border rounded px-2 py-1 h-100 d-flex align-items-center flex-wrap gap-1 ${filtrosActivos.length > 0 ? "border-primary bg-light" : ""}`}
                            style={{ minHeight: 38 }}
                        >
                            <span className="fw-semibold text-secondary small text-nowrap me-1">
                                <i className="bi bi-funnel me-1"></i>
                                Filtros activos
                                <span className={`badge ms-1 ${filtrosActivos.length > 0 ? "bg-primary" : "bg-secondary"}`}>
                                    {filtrosActivos.length}
                                </span>
                            </span>

                            {filtrosActivos.length === 0 && (
                                <span className="text-muted small fst-italic">Ninguno</span>
                            )}

                            {filtrosActivos.map(item => (
                                <span
                                    key={item.id}
                                    className={`badge d-inline-flex align-items-center ${item.clase}`}
                                    style={{ fontWeight: 400, maxWidth: "100%" }}
                                    title={`${item.label}: ${item.valor}`}
                                >
                                    <i className={`bi ${item.icono} me-1`} style={{ fontSize: "0.7rem" }}></i>
                                    <span className="text-truncate" style={{ maxWidth: 180 }}>
                                        {item.label}: <strong>{item.valor}</strong>
                                    </span>
                                    <button
                                        type="button"
                                        className="btn p-0 ms-1 border-0 lh-1 text-reset"
                                        style={{ fontSize: "0.85rem", opacity: 0.8 }}
                                        onClick={item.onClear}
                                        title="Quitar este filtro"
                                        aria-label={`Quitar filtro ${item.label}`}
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}

                            {filtrosActivos.length > 0 && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger py-0 px-2 ms-1"
                                        style={{ fontSize: "0.75rem" }}
                                        onClick={limpiarFiltros}
                                    >
                                        <i className="bi bi-x-circle me-1"></i>
                                        Limpiar todo
                                    </button>
                                    <span className="text-muted small ms-1 text-nowrap">
                                        {filteredData.length} de {limitedData.length} registros
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {showCsvExport && (
                    <div className="col-md-2">
                        <button className="btn btn-success" onClick={exportToCSV}>Exportar Datos</button>
                    </div>
                )}
                <div className={showCsvExport ? "col-md-4" : "col-md-6"}>
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar"
                            value={globalFilter}
                            onChange={e => setGlobalFilter(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="position-relative">
                {loading && (
                    <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-2"
                        style={{ background: "rgba(255,255,255,0.75)", zIndex: 1000, minHeight: 120 }}
                    >
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                        <span className="text-secondary small fw-semibold">Cargando...</span>
                    </div>
                )}
                <div className="table-responsive" style={{ overflowX: "auto", width: "100%" }}>
                    <table
                        className={normalizedTableClassName}
                        style={{ tableLayout: virtualize ? "fixed" : "auto", width: "100%" }}
                    >
                        <thead style={{ position: "sticky", top: stickyTop, zIndex: 999, background: "#fff" }}>
                            {groupHeaders.length > 0 && (
                            <tr>
                                {groupHeaders.map((gh, i) => (
                                    <th
                                        key={`group-${i}`}
                                        colSpan={gh.colSpan}
                                        className={gh.className || "text-center"}
                                        style={{
                                            textAlign: "center",
                                            backgroundColor: gh.background || "#f8f9fa",
                                            fontWeight: "bold",
                                            position: stickyHeader ? "sticky" : "initial",
                                            top: 0,
                                            zIndex: 999
                                        }}
                                    >
                                        {gh.title}
                                    </th>
                                ))}
                            </tr>
                        )}
                        <tr>
                            {columns.map((col, i) => (
                                <th
                                    key={i}
                                    className={col.headerClassName || ""}
                                    style={{
                                        ...thBaseStyle,
                                        width: col.width || "auto",
                                        minWidth: col.width || "auto",
                                        maxWidth: col.width || "auto",
                                        position: "sticky",
                                        top: groupHeaders.length > 0 ? `${headerHeight}px` : 0,
                                        background: "#fff",
                                        zIndex: 2
                                    }}
                                    onClick={() => handleSort(col.name)}
                                >
                                    {col.title}{renderSortIcon(col.name)}
                                </th>
                            ))}
                        </tr>
                        <tr>
                            {columns.map((col, i) => (
                                <th
                                    key={i}
                                    className={col.headerClassName || ""}
                                    style={{
                                        ...thBaseStyle,
                                        position: "sticky",
                                        top: groupHeaders.length > 0 ? `${headerHeight * 2}px` : `${headerHeight}px`,
                                        background: "#fff",
                                        zIndex: 2
                                    }}
                                >
                                    {col.filterable !== false && (
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder={`Filtrar ${col.title}`}
                                            value={columnFilters[col.name] || ""}
                                            onChange={e => handleColumnFilterChange(col.name, e.target.value)}
                                        />
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    {/* --------- TBody: virtualizado opcional --------- */}
                    <tbody ref={tbodyRef}>
                        {virtualize && topSpacer > 0 && (
                            <tr aria-hidden="true">
                                <td colSpan={columns.length} style={{ height: topSpacer, padding: 0, border: 0 }} />
                            </tr>
                        )}

                        {paginatedData.length > 0
                            ? (virtualize ? renderVirtualRows() : renderNormalRows())
                            : (
                                <tr>
                                    <td colSpan={columns.length} className="text-center">No hay datos para mostrar</td>
                                </tr>
                            )
                        }

                        {virtualize && bottomSpacer > 0 && (
                            <tr aria-hidden="true">
                                <td colSpan={columns.length} style={{ height: bottomSpacer, padding: 0, border: 0 }} />
                            </tr>
                        )}
                    </tbody>
                </table>
                </div>
            </div>

            <div className="row mt-3">
                <div className="col-md-4" />
                <div className="col-md-4 d-flex justify-content-end">{renderPagination()}</div>
                <div className="col-md-4 d-flex align-items-center justify-content-center">
                    <select
                        className="form-select me-2"
                        style={{ width: "auto" }}
                        value={itemsPerPage}
                        onChange={e => setItemsPerPage(Number(e.target.value))}
                    >
                        {itemsPerPageOptions.map(opt => (<option key={opt} value={opt}>{opt} por página</option>))}
                    </select>
                    <span>
                        Mostrando {virtualize ? Math.max(0, end - start) : paginatedData.length} de {filteredData.length} registros
                    </span>
                </div>
            </div>
        </div>
    )
})

GdTable.displayName = "GdTable"

export default GdTable
